import { useRef, type KeyboardEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/Popover'
import { Button } from '@/components/ui/Button'
import type { Box } from '@/services/tour/tourModel'
import {
  BACK,
  CLOSE_TOUR,
  DONE,
  END_TOUR,
  MISSING_DEFAULT,
  NEXT,
  SKIP,
  WHAT_YOULL_SEE,
  orPress,
  stepCount,
  tourName,
} from './tourText'

interface CoachCardProps {
  title: string
  text: string
  step: number
  total: number
  /** Where the card points: the target, or in the missing state the fallback. */
  anchor: Box | null
  missing: boolean
  whenMissing?: string
  /** A do step's control: "Or press <name>." under the text (SA-1316). */
  pressName?: string
  /** "What you'll see" under the text. */
  expect?: string
  /** A read step: the card sits in the bottom corner, where the panel was. */
  corner?: boolean
  reducedMotion: boolean
  onNext: () => void
  onBack: () => void
  onExit: () => void
}

function inField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))
}

/**
 * The coach card (spec §1, §2): a non-modal dialog beside the target, holding the step's
 * words, "2 of 5", Back, Next (the one primary button) and ✕. Keys are taken only while
 * focus is inside it; Escape leaves from there too.
 */
export function CoachCard(props: CoachCardProps) {
  const { title, step, total, anchor, missing, reducedMotion, onNext, onBack, onExit } = props
  const content = useRef<HTMLDivElement>(null)
  const last = step === total - 1
  const name = tourName(title)

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (inField(e.target)) return
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      onNext()
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      onBack()
    }
  }

  // A read step sits in the corner; with nothing to point at the card sits under the top of the page.
  const at = props.corner
    ? { top: window.innerHeight - 16, left: window.innerWidth - 16, width: 0, height: 0 }
    : (anchor ?? { top: 80, left: window.innerWidth / 2, width: 0, height: 0 })

  return (
    <Popover open>
      <PopoverAnchor asChild>
        <div
          aria-hidden="true"
          className="pointer-events-none fixed"
          style={{ top: at.top, left: at.left, width: at.width, height: at.height }}
        />
      </PopoverAnchor>
      <PopoverContent
        ref={content}
        role="dialog"
        aria-label={name}
        aria-modal={false}
        tabIndex={-1}
        side={props.corner ? 'top' : 'bottom'}
        align={props.corner ? 'end' : 'start'}
        sideOffset={12}
        collisionPadding={16}
        data-testid="tour-card"
        data-tour-layer=""
        className="w-[min(320px,calc(100vw-32px))] p-4 focus-visible:outline-2 focus-visible:outline-ring"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          content.current?.focus({ preventScroll: true })
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          e.preventDefault()
          if (content.current?.contains(document.activeElement)) onExit()
        }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs font-medium text-muted-foreground">
            {name} · {stepCount(step, total)}
          </p>
          <button
            type="button"
            aria-label={CLOSE_TOUR}
            onClick={onExit}
            className="-m-1 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={`${step}:${missing ? 'missing' : 'shown'}`}
            data-testid="tour-card-text"
            className="mt-2 text-sm text-popover-foreground"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reducedMotion ? 0.12 : 0.18 }}
          >
            {missing ? props.whenMissing || MISSING_DEFAULT : props.text}
          </motion.p>
        </AnimatePresence>
        {!missing && props.pressName && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid="tour-card-press">
            {orPress(props.pressName)}
          </p>
        )}
        {!missing && props.expect && (
          <div className="mt-3 border-t border-border pt-2" data-testid="tour-card-expect">
            <p className="text-xs font-medium text-muted-foreground">{WHAT_YOULL_SEE}</p>
            <p className="mt-1 text-xs text-popover-foreground">{props.expect}</p>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          {missing ? (
            <>
              <Button variant="ghost" size="sm" onClick={onExit}>
                {END_TOUR}
              </Button>
              <Button size="sm" onClick={onNext}>
                {last ? DONE : SKIP}
              </Button>
            </>
          ) : (
            <>
              {step > 0 && (
                <Button variant="ghost" size="sm" onClick={onBack}>
                  {BACK}
                </Button>
              )}
              <Button size="sm" onClick={onNext}>
                {last ? DONE : NEXT}
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
